import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "../supabase";

const F = "'Helvetica Neue', Helvetica, Arial, sans-serif";

function formatDate(d) {
  if (!d) return "";
  return new Date(d + "T00:00:00").toLocaleDateString("he-IL", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function TypeBadge({ post }) {
  const isAlbum = post.title?.includes("אלבום") || post.excerpt?.includes("אלבום");
  const label = isAlbum ? "Album" : "Single";
  return (
    <span style={{
      fontFamily: F, fontSize: "8px", fontWeight: 700, letterSpacing: "0.3em",
      textTransform: "uppercase", padding: "3px 8px",
      border: "1px solid rgba(240,237,232,0.3)",
      color: "#f0ede8", opacity: 0.7,
    }}>{label}</span>
  );
}

/* ── Slideshow Featured ── */
function FeaturedSlideshow({ posts }) {
  const [active, setActive] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  const goTo = useCallback((idx) => {
    if (idx === active) return;
    setTransitioning(true);
    setTimeout(() => {
      setActive(idx);
      setTransitioning(false);
    }, 350);
  }, [active]);

  useEffect(() => {
    const id = setInterval(() => {
      setTransitioning(true);
      setTimeout(() => {
        setActive(prev => (prev + 1) % posts.length);
        setTransitioning(false);
      }, 350);
    }, 5000);
    return () => clearInterval(id);
  }, [posts.length]);

  const post = posts[active];

  return (
    <div style={{ position: "relative", width: "100%", aspectRatio: "21/9", overflow: "hidden", background: "#000" }}>
      {/* background image */}
      <div style={{
        position: "absolute", inset: 0,
        opacity: transitioning ? 0 : 1,
        transition: "opacity 0.35s ease",
      }}>
        {post.cover_url && (
          <img
            src={post.cover_url}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", filter: "brightness(0.45)" }}
          />
        )}
      </div>

      {/* gradient */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(to right, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)",
      }} />
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 40%)",
      }} />

      {/* content */}
      <div style={{
        position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        justifyContent: "center", padding: "clamp(24px, 5vw, 64px)",
        maxWidth: "600px",
        opacity: transitioning ? 0 : 1,
        transform: transitioning ? "translateY(8px)" : "translateY(0)",
        transition: "opacity 0.35s ease, transform 0.35s ease",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
          <TypeBadge post={post} />
          <span style={{ fontFamily: F, fontSize: "8px", letterSpacing: "0.25em", textTransform: "uppercase", color: "#f0ede8", opacity: 0.4 }}>
            {post.artist}
          </span>
        </div>
        <Link to={`/press/${post.slug}`} style={{ textDecoration: "none", color: "inherit" }}>
          <h2 style={{
            fontFamily: F, fontSize: "clamp(22px, 3.5vw, 42px)", fontWeight: 900,
            letterSpacing: "-0.02em", lineHeight: 1.05, color: "#f0ede8",
            marginBottom: "14px",
          }}>
            {post.title}
          </h2>
        </Link>
        {post.excerpt && (
          <p style={{
            fontFamily: F, fontSize: "13px", fontWeight: 300, lineHeight: 1.75,
            color: "rgba(240,237,232,0.55)", maxWidth: "440px",
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {post.excerpt}
          </p>
        )}
        <Link to={`/press/${post.slug}`} style={{
          display: "inline-flex", alignItems: "center", gap: "8px", marginTop: "20px",
          fontFamily: F, fontSize: "9px", fontWeight: 700, letterSpacing: "0.25em",
          textTransform: "uppercase", color: "#f0ede8", textDecoration: "none", opacity: 0.6,
          transition: "opacity 0.2s",
        }}
          onMouseOver={e => e.currentTarget.style.opacity = 1}
          onMouseOut={e => e.currentTarget.style.opacity = 0.6}
        >
          Read →
        </Link>
      </div>

      {/* slide counter top right */}
      <div style={{
        position: "absolute", top: "20px", right: "24px",
        fontFamily: "monospace", fontSize: "11px", color: "rgba(240,237,232,0.3)",
        letterSpacing: "0.1em",
      }}>
        {String(active + 1).padStart(2, "0")} / {String(posts.length).padStart(2, "0")}
      </div>

      {/* dots */}
      <div style={{
        position: "absolute", bottom: "20px", right: "24px",
        display: "flex", gap: "6px", alignItems: "center",
      }}>
        {posts.map((_, i) => (
          <button key={i} onClick={() => goTo(i)} style={{
            width: i === active ? "20px" : "6px", height: "2px",
            background: i === active ? "#f0ede8" : "rgba(240,237,232,0.3)",
            border: "none", cursor: "pointer", padding: 0,
            transition: "all 0.3s ease", borderRadius: "2px",
          }} />
        ))}
      </div>

      {/* date bottom left */}
      <div style={{
        position: "absolute", bottom: "20px", left: "clamp(24px, 5vw, 64px)",
        fontFamily: F, fontSize: "8px", letterSpacing: "0.25em",
        textTransform: "uppercase", color: "rgba(240,237,232,0.3)",
      }}>
        {formatDate(post.date)}
      </div>
    </div>
  );
}

/* ── Grid card ── */
function GridCard({ post, index }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={`/press/${post.slug}`}
      style={{ textDecoration: "none", color: "#f0ede8", display: "block" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <article style={{
        borderBottom: "1px solid rgba(240,237,232,0.07)",
        animation: `yen-press-in 0.4s ${index * 0.06}s ease both`,
      }}>
        {/* image */}
        <div style={{ position: "relative", overflow: "hidden", background: "#0a0a0a", aspectRatio: "3/2" }}>
          {post.cover_url && (
            <img
              src={post.cover_url}
              alt={post.title}
              style={{
                width: "100%", height: "100%", objectFit: "cover", display: "block",
                transform: hovered ? "scale(1.04)" : "scale(1)",
                filter: hovered ? "brightness(0.85) grayscale(0)" : "brightness(0.7) grayscale(0.15)",
                transition: "transform 0.6s cubic-bezier(0.4,0,0.2,1), filter 0.4s ease",
              }}
            />
          )}
          {/* type badge on image */}
          <div style={{ position: "absolute", top: "12px", left: "12px" }}>
            <TypeBadge post={post} />
          </div>
        </div>

        {/* text */}
        <div style={{ padding: "16px 0 20px" }}>
          <p style={{
            fontFamily: F, fontSize: "8px", letterSpacing: "0.22em",
            textTransform: "uppercase", color: "#f0ede8", opacity: 0.3,
            marginBottom: "8px",
          }}>
            {post.artist && <span>{post.artist} · </span>}{formatDate(post.date)}
          </p>
          <h3 style={{
            fontFamily: F, fontSize: "15px", fontWeight: 700,
            letterSpacing: "-0.01em", lineHeight: 1.2, color: "#f0ede8",
            display: "-webkit-box", WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {post.title}
          </h3>
          <div style={{
            marginTop: "10px",
            fontFamily: F, fontSize: "8px", fontWeight: 700, letterSpacing: "0.2em",
            textTransform: "uppercase", color: "#f0ede8",
            opacity: hovered ? 0.6 : 0,
            transform: hovered ? "translateX(0)" : "translateX(-4px)",
            transition: "opacity 0.2s, transform 0.2s",
          }}>
            Read →
          </div>
        </div>
      </article>
    </Link>
  );
}

/* ── Artist filter tab ── */
function FilterTab({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      fontFamily: F, fontSize: "9px", fontWeight: active ? 700 : 400,
      letterSpacing: "0.18em", textTransform: "uppercase",
      padding: "10px 0", marginRight: "28px",
      background: "transparent", border: "none",
      borderBottom: active ? "1px solid #f0ede8" : "1px solid transparent",
      color: active ? "#f0ede8" : "rgba(240,237,232,0.35)",
      cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
    }}>
      {label}
    </button>
  );
}

/* ══════════════════════════════════════════
   MAIN
══════════════════════════════════════════ */
export default function Press() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [artistFilter, setArtistFilter] = useState("All");

  useEffect(() => {
    async function fetchPosts() {
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from("press_posts")
        .select("id, title, artist, cover_url, date, excerpt, slug, publish_at")
        .order("date", { ascending: false });
      if (error) { setError(error.message); }
      else {
        // Filter out posts scheduled for the future
        const visible = (data || []).filter(p =>
          !p.publish_at || p.publish_at <= today
        );
        setPosts(visible);
      }
      setLoading(false);
    }
    fetchPosts();
  }, []);

  const artists = useMemo(() => {
    const names = new Set();
    posts.forEach(p => {
      if (p.artist) p.artist.split(",").forEach(a => names.add(a.trim()));
    });
    return ["All", ...Array.from(names).sort()];
  }, [posts]);

  const filtered = useMemo(() => {
    if (artistFilter === "All") return posts;
    return posts.filter(p =>
      p.artist && p.artist.split(",").map(a => a.trim()).includes(artistFilter)
    );
  }, [posts, artistFilter]);

  const featured = filtered.slice(0, 3).filter(p => p.cover_url);
  const grid = filtered.slice(featured.length > 0 ? 3 : 0);

  return (
    <>
      <Helmet>
        <title>Press — YEN SOUND</title>
        <meta name="description" content="כתבות, פיצ'רים ועדכונים מאמני YEN SOUND." />
        <meta property="og:title" content="Press — YEN SOUND" />
        <meta property="og:description" content="כתבות, פיצ'רים ועדכונים מאמני YEN SOUND." />
      </Helmet>

      <style>{`
        @keyframes yen-press-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div style={{ backgroundColor: "#000", minHeight: "100vh", paddingTop: "60px" }}>

        {loading && (
          <p style={{ fontFamily: F, fontSize: "9px", letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.2, padding: "80px 32px" }}>
            Loading...
          </p>
        )}

        {!loading && error && (
          <p style={{ fontFamily: F, fontSize: "10px", color: "rgba(255,100,100,0.7)", padding: "80px 32px" }}>{error}</p>
        )}

        {!loading && !error && filtered.length > 0 && (
          <>
            {/* ── Slideshow ── */}
            {featured.length > 0 && <FeaturedSlideshow posts={featured} />}

            {/* ── Filter tabs + header ── */}
            <div style={{
              padding: "0 32px",
              borderBottom: "1px solid rgba(240,237,232,0.08)",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{
                display: "flex", overflowX: "auto",
                scrollbarWidth: "none", msOverflowStyle: "none",
              }}>
                {artists.length > 2 && artists.map(a => (
                  <FilterTab
                    key={a}
                    label={a}
                    active={artistFilter === a}
                    onClick={() => setArtistFilter(a)}
                  />
                ))}
              </div>
              <span style={{ fontFamily: F, fontSize: "9px", color: "rgba(240,237,232,0.2)", letterSpacing: "0.1em", flexShrink: 0, paddingLeft: "16px" }}>
                {filtered.length} {filtered.length === 1 ? "release" : "releases"}
              </span>
            </div>

            {/* ── 2-col grid ── */}
            {grid.length > 0 && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "0",
                padding: "32px 32px 0",
                maxWidth: "1200px",
                margin: "0 auto",
              }}>
                {grid.map((post, i) => (
                  <div key={post.id} style={{
                    padding: "0 20px",
                    borderRight: i % 2 === 0 ? "1px solid rgba(240,237,232,0.07)" : "none",
                  }}>
                    <GridCard post={post} index={i} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {!loading && !error && filtered.length === 0 && (
          <p style={{ fontFamily: F, fontSize: "9px", letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.2, padding: "80px 32px" }}>
            No posts yet.
          </p>
        )}

        <div style={{ height: "100px" }} />
      </div>
    </>
  );
}
